import { useEffect } from 'react';
import {
  generatePath,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useAppSelector } from '@store';
import { Account, ButtonPseudo, ButtonSize, PermissionEnum } from '@ui/src';
import { CustomerStatus, OrganizationType } from '../types';
import useCustomerAccounts from './hooks/use-accounts';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import Card from '../../../components/common/card/card';
import AccountCurrencies from './parts/currencies';
import AccountsTransactionStatement from '../statements/accounts';
import CustomerAccountsPanels from './parts/panels';
import AccountItem from './parts/item';

import ROUTES from '../../../routes';

import cn from './styles.module.css';
import commonStyles from '../common.module.css';

const CustomerAccounts = ({ type }: { type: OrganizationType }) => {
  const { cardsEnabled, overdraftEnabled } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const {
    baseInformation: individualBaseInformation,
    baseInformationIsLoading: individualBalseInformationLoading,
    balances: individualBalances,
  } = useAppSelector((state) => state.individualCustomers.info.general);
  const { closedBalances } = useAppSelector((state) => state.customersAccounts);
  const {
    baseInformation: corporateBaseInformation,
    baseInformationIsLoading: corporateBalseInformationLoading,
    balances: corporateBalances,
  } = useAppSelector((state) => state.corporateCustomers.info.general);

  const { activePermissions } = useAppSelector((state) => state.root);

  const individualMatch = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS);
  const corporateMatch = useMatch(ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS);

  const match =
    type === OrganizationType.INDIVIDUAL ? individualMatch : corporateMatch;
  const accountsLink =
    type === OrganizationType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_TRANSACTIONS
      : ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS_TRANSACTIONS;

  const baseInformation =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;
  const baseInformationIsLoading =
    type === OrganizationType.INDIVIDUAL
      ? individualBalseInformationLoading
      : corporateBalseInformationLoading;
  const balances =
    type === OrganizationType.INDIVIDUAL
      ? individualBalances
      : corporateBalances;

  const { customerId } = useParams();

  const navigate = useNavigate();

  const {
    showCurrencies,
    setShowCurrencies,
    setPopupVisible,
    onBlockTopup,
    onBlockWithdraw,
    onClose,
    closeInProgress,
    openInProgress,
    blockCreditInProgress,
    unblockCreditInProgress,
    blockDebitInProgress,
    unblockDebitInProgress,
    setTopUpPanelIsOpen,
    closeIsSuccess,
    openIsSuccess,
    blockDebitIsSuccess,
    unblockDebitIsSuccess,
    blockCreditIsSuccess,
    unblockCreditIsSuccess,
    closeOpenError,
    blockUblockTopupError,
    blockUblockWithdrawalsError,
    setStatementPanelVisible,
    setSelectedAccount,
    setCurrenciesList,
    onUpdateVisibility,
    updateVisiblityInProgress,
    accountBalances,
    setBalances,
    defaultBalances,
    setDefaultBalances,
    onUpdateOverdraftBalanceValue,
    updateOverdraftBalanceIsLoading,
    topUpPanelIsOpen,
    selectedTopUpId,
    setSelectedTopUpId,
    setSelectedPrimaryAccount,
    setVisibleToClient,
    onCreateAccount,
    createAccountInProgress,
    popupVisible,
    primariesAccounts,
    selectedPrimaryAccount,
    visibleToClient,
    statementPanelVisible,
    onCloseStatementPanel,
    statementError,
    isLoading,
    isFetching,
    selectedCurrency,
    selectedDateType,
    selectedFormat,
    selectedDateRange,
    onDownloadStatement,
    currenciesList,
    setSelectedCurrency,
    setSelectedDateType,
    setSelectedDateRange,
    setSelectedFormat,
    selectedAccount,
  } = useCustomerAccounts({
    customerId,
    baseInformation,
    type,
  });

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(accountsLink, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    let title = '';

    if (type === OrganizationType.INDIVIDUAL) {
      title =
        (baseInformation as Partial<GeneralInfo>)?.firstName ||
        (baseInformation as Partial<GeneralInfo>)?.lastName
          ? `${(baseInformation as Partial<GeneralInfo>).firstName} ${(baseInformation as Partial<GeneralInfo>).lastName || ''}: Accounts`
          : 'Accounts';
    } else if (type === OrganizationType.CORPORATE) {
      title = (baseInformation as Partial<CorporateGeneralInfo>)?.name
        ? `${(baseInformation as Partial<CorporateGeneralInfo>)?.name}: Accounts`
        : 'Accounts';
    }

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation, baseInformation, type]);

  let accountsContent;

  if (showCurrencies) {
    accountsContent = (
      <AccountCurrencies
        setSelectedAccount={setSelectedAccount}
        setShowCurrencies={setShowCurrencies}
        selectedAccount={selectedAccount}
        accounts={baseInformation?.accounts}
        status={baseInformation?.status}
      />
    );
  } else {
    accountsContent = (
      <>
        <div className={cn.accountsItems}>
          <div className={cn.accountsItemsTitle}>
            <div className={cn.accountsItemsTitleText}>Accounts</div>
            {baseInformation?.status !== CustomerStatus.CLOSED && (
              <ButtonPseudo
                text="+ Add new account"
                className={cn.addAccountLink}
                size={ButtonSize.SMALL}
                onClick={() => setPopupVisible(true)}
              />
            )}
          </div>
          {baseInformationIsLoading ? (
            <Card isLoading={baseInformationIsLoading} />
          ) : (
            (baseInformation?.accounts as Account[])?.map((item) => (
              <AccountItem
                key={item.id}
                account={item as any}
                balance={balances?.filter(
                  (balance) => balance.accountId === item.id,
                )}
                onBlockTopup={onBlockTopup}
                onBlockWithdraw={onBlockWithdraw}
                onClose={onClose}
                closeInProgress={closeInProgress || openInProgress}
                blockTopupInProgress={
                  blockCreditInProgress || unblockCreditInProgress
                }
                blockWithdrawInProgress={
                  blockDebitInProgress || unblockDebitInProgress
                }
                setTopUpPanelIsOpen={setTopUpPanelIsOpen}
                closeIsSuccess={closeIsSuccess}
                openIsSuccess={openIsSuccess}
                blockDebitIsSuccess={blockDebitIsSuccess}
                unblockDebitIsSuccess={unblockDebitIsSuccess}
                blockCreditIsSuccess={blockCreditIsSuccess}
                unblockCreditIsSuccess={unblockCreditIsSuccess}
                closeOpenError={closeOpenError}
                blockUblockTopupError={blockUblockTopupError}
                blockUblockWithdrawalsError={blockUblockWithdrawalsError}
                setShowCurrencies={setShowCurrencies}
                setStatementPanelVisible={setStatementPanelVisible}
                setSelectedAccount={setSelectedAccount}
                setCurrenciesList={setCurrenciesList}
                onUpdateVisibility={onUpdateVisibility}
                updateVisibilityInProgress={updateVisiblityInProgress}
                overdraftEnabled={overdraftEnabled}
                accountsBalances={accountBalances}
                setBalances={setBalances}
                defaultBalances={defaultBalances}
                setDefaultBalances={setDefaultBalances}
                onUpdateOverdraftBalanceValue={onUpdateOverdraftBalanceValue}
                updateOverdraftBalanceIsLoading={
                  updateOverdraftBalanceIsLoading
                }
                status={baseInformation?.status as CustomerStatus}
                closedBalances={closedBalances}
                selectedAccount={selectedAccount}
              />
            ))
          )}
        </div>
        {activePermissions?.includes(
          PermissionEnum.CUSTOMERS_STATEMENTS_LIST,
        ) && (
          <AccountsTransactionStatement
            title="Statement of account"
            transactionsPath={
              type === OrganizationType.INDIVIDUAL
                ? ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_TRANSACTIONS
                : ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS_TRANSACTIONS
            }
            cardsTransactionsPath={
              type === OrganizationType.INDIVIDUAL
                ? ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_CARDS
                : ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS_CARDS
            }
            accounts={
              baseInformation?.id === customerId
                ? baseInformation?.accounts
                : undefined
            }
            cards={baseInformation?.cards}
            cardsEnabled={cardsEnabled}
            balances={balances}
            accountsPage
          />
        )}
      </>
    );
  }

  return (
    <div className={commonStyles.customerContent}>
      {accountsContent}
      <CustomerAccountsPanels
        topUpPanelIsOpen={topUpPanelIsOpen}
        setTopUpPanelIsOpen={setTopUpPanelIsOpen}
        selectedTopUpId={selectedTopUpId}
        setSelectedTopUpId={setSelectedTopUpId}
        setSelectedAccount={setSelectedAccount}
        baseInformation={baseInformation}
        setPopupVisible={setPopupVisible}
        setSelectedPrimaryAccount={setSelectedPrimaryAccount}
        setVisibleToClient={setVisibleToClient}
        onCreateAccount={onCreateAccount}
        createAccountInProgress={createAccountInProgress}
        popupVisible={popupVisible}
        primariesAccounts={primariesAccounts}
        selectedPrimaryAccount={selectedPrimaryAccount}
        visibleToClient={visibleToClient}
        statementPanelVisible={statementPanelVisible}
        onCloseStatementPanel={onCloseStatementPanel}
        statementError={statementError}
        isLoading={isLoading}
        isFetching={isFetching}
        selectedCurrency={selectedCurrency}
        selectedDateType={selectedDateType}
        selectedFormat={selectedFormat}
        selectedDateRange={selectedDateRange}
        onDownloadStatement={onDownloadStatement}
        currenciesList={currenciesList}
        setSelectedCurrency={setSelectedCurrency}
        setSelectedDateType={setSelectedDateType}
        setSelectedDateRange={setSelectedDateRange}
        setSelectedFormat={setSelectedFormat}
        selectedAccount={selectedAccount}
      />
    </div>
  );
};

export default CustomerAccounts;
